import naja from 'naja';

export class Form {
    static init(): void {
        Form.validation();
        Form.handleFixedLabel();
        Form.handleInputFiles();
        Form.handleCheckboxes();
        Form.handlePassValidation();
        Form.formLabels();
    }

    // trigger form validation
    static validation(): void {
        const formsToValidate: NodeListOf<HTMLFormElement> = document.querySelectorAll('[data-validation]');
        if (formsToValidate.length > 0) {
            formsToValidate.forEach((form) => {
                if (form.dataset.validation === 'onLoad') {
                    Form.validateForm(form);
                }

                const requiredControls: NodeListOf<HTMLInputElement> = form.querySelectorAll('[data-required]');
                requiredControls.forEach((control) => {
                    Form.listenControl(control);
                });

                const requiredSelectboxes: NodeListOf<HTMLInputElement> =
                    form.querySelectorAll('[data-requiredSelectbox]');

                requiredSelectboxes.forEach((selectbox: HTMLInputElement) => {
                    if (selectbox.dataset.requiredselectbox) {
                        const dropdown = document.getElementById(selectbox.dataset.requiredselectbox);
                        if (dropdown) {
                            const options = dropdown.querySelectorAll('.box-dropdown__option');

                            options.forEach((option) => {
                                option.addEventListener('click', () => {
                                    if (selectbox.parentElement) {
                                        setTimeout(function () {
                                            const container = selectbox.parentElement;
                                            if (container) {
                                                if (selectbox.value) {
                                                    container.classList.remove('form-container--error');
                                                } else {
                                                    container.classList.add('form-container--error');
                                                }
                                            }
                                        }, 100);
                                    }
                                });
                            });
                        }
                    }
                });

                form.addEventListener('submit', function (e) {
                    e.preventDefault();

                    Form.validateForm(form);

                    const errors = document.querySelectorAll('.form-container--error');
                    if (errors.length === 0) {
                        form.submit();
                    }
                });
            });
        }
    }

    // bind validation on form controls
    static validateForm(form: HTMLFormElement): void {
        const requiredControls: NodeListOf<HTMLInputElement> = form.querySelectorAll('[data-required]');

        if (requiredControls.length > 0) {
            requiredControls.forEach((control) => {
                Form.validateControl(control);
            });
        }
    }

    // check email format
    static validateEmail(email: string): boolean {
        const re =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(email);
    }

    // validate form controls
    static validateControl(control: HTMLInputElement): void {
        const parent = control.closest('.form-container');

        const isEmail = control.dataset.required === 'email';
        const isCheckbox = control.type === 'checkbox';
        const isNumChar = control.dataset.required === 'numLetter';
        const isPostCode = control.dataset.required === 'postCode';
        const isTel = control.dataset.required === 'tel';

        if (parent) {
            if (isCheckbox) {
                if (control.checked) {
                    parent.classList.remove('form-container--error');
                    parent.classList.add('form-container--success');
                } else {
                    parent.classList.add('form-container--error');
                    parent.classList.remove('form-container--success');
                }
            } else if (control.value === '') {
                parent.classList.add('form-container--error');
                parent.classList.remove('form-container--success');
            } else if (isEmail) {
                if (this.validateEmail(control.value)) {
                    parent.classList.remove('form-container--error');
                    parent.classList.add('form-container--success');
                } else {
                    parent.classList.add('form-container--error');
                    parent.classList.remove('form-container--success');
                }
            } else if (isNumChar) {
                if (/(?=.*?[0-9])(?=.*?[A-Za-z])(?=.*[^0-9A-Za-z]).+/g.test(control.value)) {
                    parent.classList.remove('form-container--error');
                    parent.classList.add('form-container--success');
                } else {
                    parent.classList.add('form-container--error');
                    parent.classList.remove('form-container--success');
                }
            } else if (isPostCode) {
                control.value = control.value.replace(/[^0-9.]/g, '');
                control.value = control.value.replace(/(\..*)\./g, '$1');
                if (control.value.length === 5) {
                    parent.classList.remove('form-container--error');
                    parent.classList.add('form-container--success');
                    control.value = control.value.split(/(?=.{2}$)/).join(' ');
                } else {
                    parent.classList.add('form-container--error');
                    parent.classList.remove('form-container--success');
                }
            } else if (isTel) {
                control.value = control.value.replace(/[^0-9.]/g, '');
                control.value = control.value.replace(/(\..*)\./g, '$1');

                if (control.value.length >= 9) {
                    parent.classList.remove('form-container--error');
                    parent.classList.add('form-container--success');
                    control.value = control.value.split(/(?=.{3}$)/).join(' ');
                    control.value = control.value.split(/(?=.{7}$)/).join(' ');
                    control.value = control.value.split(/(?=.{11}$)/).join(' ');
                } else {
                    parent.classList.add('form-container--error');
                    parent.classList.remove('form-container--success');
                }
            } else {
                parent.classList.remove('form-container--error');
                parent.classList.add('form-container--success');
            }
        }
    }

    // listen form control to allow only letters
    static listenControl(control: HTMLInputElement): void {
        const onlyLetters = control.dataset.required === 'onlyLetters';

        if (control.value) {
            Form.validateControl(control);
        }

        control.addEventListener('input', () => {
            Form.validateControl(control);

            if (onlyLetters) {
                if (!/^[a-zA-Zá-ž ]+$/.test(control.value)) {
                    control.value = control.value.slice(0, -1);
                }
            }
        });
    }

    // handle fixed label on page load (because of default values)
    static handleFixedLabel(): void {
        const labels = document.querySelectorAll('.form-fixed-label');
        const activeClass = 'form-fixed-label--active';

        if (labels.length > 0) {
            labels.forEach((label) => {
                const parent = label.closest('.form-container');

                if (parent) {
                    const input = parent.querySelector('.form-control') as HTMLInputElement;

                    if (input) {
                        if (input.value === '') {
                            label.classList.remove(activeClass);
                        } else {
                            label.classList.add(activeClass);
                        }

                        input.addEventListener('input', () => {
                            if (input.value === '') {
                                label.classList.remove(activeClass);
                            } else {
                                label.classList.add(activeClass);
                            }
                        });
                    }
                }
            });
        }
    }

    static handleInputFiles(): void {
        const formOrderDocs = <HTMLFormElement>document.getElementById('order-docs-form');
        const formOrderDetailDocs = <HTMLFormElement>document.getElementById('order-detail-docs-form');
        const labels = document.querySelectorAll('.input-file');
        const orderSubmit: HTMLElement | null = document.getElementById('order-docs-form-submit');
        const orderDetailSubmit: HTMLElement | null = document.getElementById('order-detail-docs-form-submit');
        const uploadSubmit: HTMLElement | null = document.getElementById('order-docs-upload-submit');
        const selectWhen = <HTMLElement>document.getElementById('order-docs-state');
        const alreadyUploadedFiles = document.querySelectorAll('.basket-file');
        const warning: HTMLElement | null = document.getElementById('order-docs-form__content-warning');
        const supportQuestionSubmit: HTMLElement | null = document.getElementById('support-question-form-submit');
        const formSupportQuestionDocs = <HTMLFormElement>document.getElementById('support-question-form');
        const supportUploadSubmit: HTMLElement | null = document.getElementById('support-docs-upload-submit');
        fileRemove();

        function fileRemove() {
            const alreadyUploadedFiles = document.querySelectorAll('.basket-file');
            if (alreadyUploadedFiles.length > 0 && (orderSubmit || orderDetailSubmit || supportQuestionSubmit)) {
                alreadyUploadedFiles.forEach((alreadyUploadedFile) => {
                    const fileRemove = alreadyUploadedFile.querySelector('.basket-file__remove-link');

                    if (fileRemove) {
                        fileRemove.addEventListener('click', () => {
                            const removedFile = fileRemove.closest('.basket-file');
                            if (removedFile) {
                                removedFile.remove();
                            }

                            const alreadyUploadedFiles = document.querySelectorAll('.basket-file');
                            if (alreadyUploadedFiles.length < 1) {
                                if (orderSubmit) {
                                    labels.forEach((label) => {
                                        const wrapper = label.closest('.input-file-wrapper');
                                        if (wrapper) {
                                            wrapper.classList.remove('input-file-wrapper--uploaded');
                                        }
                                    });
                                    orderSubmit.setAttribute('disabled', 'true');
                                }
                                if (orderDetailSubmit) {
                                    orderDetailSubmit.setAttribute('disabled', 'true');
                                }
                            }
                        });
                    }
                });
            } else {
                if (orderDetailSubmit) {
                    orderDetailSubmit.setAttribute('disabled', 'true');
                }
            }
        }

        if (labels.length > 0) {
            if (orderSubmit) {
                if (alreadyUploadedFiles.length > 0) {
                    orderSubmit.removeAttribute('disabled');
                } else {
                    orderSubmit.setAttribute('disabled', 'true');
                }
                selectWhen.addEventListener('change', (e) => {
                    const target = e.target as HTMLSelectElement;
                    const alreadyUploadedFiles = document.querySelectorAll('.basket-file');
                    if (target.value === 'later' || alreadyUploadedFiles.length > 0) {
                        orderSubmit.removeAttribute('disabled');
                    } else {
                        labels.forEach((label) => {
                            const count = label.querySelector('.input-file__count');
                            if (count && count.innerHTML.length) {
                                orderSubmit.removeAttribute('disabled');
                            } else {
                                orderSubmit.setAttribute('disabled', 'true');
                            }
                        });
                    }

                    if (warning) {
                        if (target.value === 'later') {
                            warning.classList.add('order-docs-form__content-warning--show');
                        } else {
                            warning.classList.remove('order-docs-form__content-warning--show');
                        }
                    }
                });
            }

            labels.forEach((label) => {
                const input = label.querySelector('input');
                const wrapper = label.closest('.input-file-wrapper');
                const count: HTMLElement | null = label.querySelector('.input-file__count');

                if (input && wrapper && count) {
                    // const countValue = count.dataset.count;
                    label.addEventListener('dragover', (e) => {
                        e.preventDefault();
                        wrapper.classList.add('input-file-wrapper--over');
                    });
                    label.addEventListener('dragleave', () => {
                        wrapper.classList.remove('input-file-wrapper--over');
                    });

                    if (uploadSubmit && orderDetailSubmit) {
                        uploadSubmit.addEventListener('click', () => {
                            wrapper.classList.remove('input-file-wrapper--uploaded');
                            uploadSubmit.setAttribute('disabled', 'true');
                            const orderDocsInput = <HTMLInputElement>document.getElementById('order-docs');
                            orderDocsInput.value = '';
                            setTimeout(() => {
                                fileRemove();
                                orderDetailSubmit.removeAttribute('disabled');
                            }, 2000);
                        });
                    }
                    label.addEventListener('drop', (ev: any) => {
                        ev.preventDefault();

                        if (ev.dataTransfer) {
                            if (ev.dataTransfer.items) {
                                wrapper.classList.add('input-file-wrapper--uploaded');
                                wrapper.classList.remove('input-file-wrapper--over');

                                for (let i = 0; i < ev.dataTransfer.items.length; i++) {
                                    if (ev.dataTransfer.items[i].kind === 'file') {
                                        const file = ev.dataTransfer.items[i].getAsFile();
                                        if (file) {
                                            const fileReader = new FileReader();
                                            fileReader.readAsDataURL(file);
                                            fileReader.onloadend = () => {
                                                const xhr = new XMLHttpRequest();
                                                const formData = new FormData();

                                                if (orderSubmit) {
                                                    const urlUpload =
                                                        '/podklady-objednavky?do=orderDocsForm-fileUpload';
                                                    const urlRefresh =
                                                        '/podklady-objednavky?do=orderDocsForm-refreshUploaded';
                                                    xhr.open('POST', urlUpload, true);
                                                    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
                                                    formData.append('upload_preset', 'ujpu6gyk');
                                                    formData.append('file', file);
                                                    xhr.send(formData);
                                                    xhr.onloadend = () => {
                                                        naja.makeRequest('GET', urlRefresh);
                                                        setTimeout(() => {
                                                            fileRemove();
                                                        }, 2000);
                                                    };
                                                } else if (supportQuestionSubmit) {
                                                    const urlUpload = '/dotaz?do=supportQuestionForm-fileUpload';
                                                    const urlRefresh = '/dotaz?do=supportQuestionForm-refreshUploaded';
                                                    xhr.open('POST', urlUpload, true);
                                                    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
                                                    formData.append('upload_preset', 'ujpu6gyk');
                                                    formData.append('file', file);
                                                    xhr.send(formData);
                                                    xhr.onloadend = () => {
                                                        naja.makeRequest('GET', urlRefresh);
                                                        setTimeout(() => {
                                                            fileRemove();
                                                        }, 2000);
                                                    };
                                                } else {
                                                    const orderDetailDocsForm = <HTMLFormElement>(
                                                        document.getElementById('order-detail-docs-form')
                                                    );
                                                    const orderId = <string>orderDetailDocsForm.dataset.orderId;
                                                    const urlUpload =
                                                        '/detail-objednavky-' +
                                                        orderId +
                                                        '?do=orderDetail-orderDetailDocsForm-fileUpload';
                                                    const urlRefresh =
                                                        '/detail-objednavky-' +
                                                        orderId +
                                                        '?do=orderDetail-orderDetailDocsForm-refreshUploaded';
                                                    xhr.open('POST', urlUpload, true);
                                                    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
                                                    formData.append('upload_preset', 'ujpu6gyk');
                                                    formData.append('file', file);
                                                    xhr.send(formData);
                                                    xhr.onloadend = () => {
                                                        naja.makeRequest('GET', urlRefresh);
                                                        setTimeout(() => {
                                                            fileRemove();
                                                        }, 2000);
                                                    };
                                                }
                                            };
                                        }
                                    }
                                }

                                // count.innerHTML = countValue + ev.dataTransfer.items.length.toString();

                                if (orderSubmit) {
                                    orderSubmit.removeAttribute('disabled');
                                }
                                if (orderDetailSubmit) {
                                    orderDetailSubmit.removeAttribute('disabled');
                                }
                            }
                        }
                    });
                    input.addEventListener('change', (e) => {
                        const target = e.target as HTMLInputElement;

                        if (target.files) {
                            if (target.files.length > 0) {
                                wrapper.classList.add('input-file-wrapper--uploaded');
                                count.innerHTML = target.files.length.toString();

                                if (orderSubmit) {
                                    orderSubmit.removeAttribute('disabled');
                                }
                                if (orderSubmit) {
                                    naja.uiHandler.submitForm(formOrderDocs);
                                    // wrapper.classList.remove('input-file-wrapper--uploaded');
                                    setTimeout(() => {
                                        fileRemove();
                                    }, 2000);
                                }
                                if (orderDetailSubmit) {
                                    naja.uiHandler.submitForm(formOrderDetailDocs);
                                    setTimeout(() => {
                                        fileRemove();
                                        orderDetailSubmit.removeAttribute('disabled');
                                    }, 2000);
                                }
                                if (supportQuestionSubmit) {
                                    naja.uiHandler.submitForm(formSupportQuestionDocs);
                                    setTimeout(() => {
                                        fileRemove();
                                    }, 2000);
                                }
                            } else {
                                wrapper.classList.remove('input-file-wrapper--uploaded');
                            }
                        }
                    });
                }
            });
        }
    }

    /**
     * Listen changes on checkbox which toggle hidden area containing required inputs
     */
    static handleCheckboxes(): void {
        // main toggle function
        const toggleArea = (checkbox: HTMLInputElement, area: HTMLElement) => {
            // show/hide area
            if (checkbox.checked) {
                area.classList.add('none');
            } else {
                area.classList.remove('none');
            }

            // based on visibility of area set (un)required inputs
            if (area.classList.contains('none')) {
                handleRequiresInputsInsideArea(area, false);
            } else {
                handleRequiresInputsInsideArea(area, true);
            }
        };

        const checkboxes: NodeListOf<HTMLInputElement> = document.querySelectorAll('.js-toggle-area');

        if (checkboxes.length > 0) {
            checkboxes.forEach((checkbox: HTMLInputElement) => {
                if (checkbox.dataset && checkbox.dataset.area) {
                    const area = document.getElementById(checkbox.dataset.area);

                    if (area) {
                        // listen on change
                        checkbox.addEventListener('input', () => {
                            toggleArea(checkbox, area);
                        });
                    }
                }
            });
        }

        /**
         * Set (un)required inputs in toggled area
         * @param area
         * @param state - true to validate nested inputs, false to not validate nested inputs
         */
        function handleRequiresInputsInsideArea(area: HTMLElement, state: boolean) {
            // mainClass is switch to subClass
            let mainClass = 'data-required';
            let subClass = 'dump-data-required';

            // true = area is visible, we want inputs required
            if (state) {
                mainClass = 'dump-data-required';
                subClass = 'data-required';

                // false = area is hidden, we don't want its required inputs
            } else {
                // form validation is based on checking errors, have to remove them
                const errors = area.querySelectorAll('.form-container--error');

                if (errors.length > 0) {
                    errors.forEach((error) => {
                        error.classList.remove('form-container--error');
                    });
                }
            }

            // based on state set requirement by attributes
            const requires = area.querySelectorAll(`[${mainClass}]`);
            if (requires.length > 0) {
                requires.forEach((item) => {
                    const value = item.getAttribute(mainClass);
                    item.removeAttribute(mainClass);
                    item.setAttribute(subClass, value || '');
                });
            }
        }
    }

    static handlePassValidation(): void {
        const form = document.querySelector('.js-pass-validation');

        function stringContainsNumber(_input: string) {
            const string1 = String(_input);
            for (let i = 0; i < string1.length; i++) {
                if (!isNaN(parseInt(string1.charAt(i))) && !(string1.charAt(i) === ' ')) {
                    return true;
                }
            }
            return false;
        }

        function isUpper(str: string) {
            return /[A-Z]/.test(str);
        }

        function hasLowerCase(str: string) {
            return /[a-z]/.test(str);
        }

        if (form) {
            const currentPass = document.getElementById('current-pass') as HTMLInputElement | null;
            const newPass = document.getElementById('new-pass') as HTMLInputElement;
            const newPassAgain = document.getElementById('new-pass-again') as HTMLInputElement;
            const submit = form.querySelector('button');

            if (newPass && newPassAgain && submit) {
                const validation = () => {
                    // @TODO: check if currentPass is correct
                    if (
                        newPass.value.length >= 8 &&
                        newPassAgain.value.length >= 8 &&
                        newPass.value === newPassAgain.value &&
                        stringContainsNumber(newPass.value) &&
                        isUpper(newPass.value) &&
                        hasLowerCase(newPass.value)
                    ) {
                        if (currentPass) {
                            if (currentPass.value !== newPass.value) {
                                submit.removeAttribute('disabled');
                            } else {
                                submit.setAttribute('disabled', 'true');
                            }
                        } else {
                            submit.removeAttribute('disabled');
                        }
                    } else {
                        submit.setAttribute('disabled', 'true');
                    }
                };

                if (currentPass) {
                    currentPass.addEventListener('input', validation);
                }
                newPass.addEventListener('input', validation);
                newPassAgain.addEventListener('input', validation);
            }
        }
    }

    static formLabels(): void {
         // Check if the script should run (for example, based on the existence of a specific element)
        const formElement = document.querySelector('.base-form-item');
        if (!formElement) {
            console.log('Form element not found. Script will not run.');
            return;
        }

        const inputFields: NodeListOf<HTMLInputElement> = document.querySelectorAll('.base-form-item__input');

        inputFields.forEach(input => {
            // Check if there's already value on page load
            if (input.value) {
                input.closest('.base-form-item')?.classList.add('focused');
            }

            // Add event listener for focus event
            input.addEventListener('focus', () => {
                input.closest('.base-form-item')?.classList.add('focused');
            });

            // Add event listener for blur event
            input.addEventListener('blur', () => {
                // Remove 'focused' class only if input is empty
                if (!input.value) {
                    input.closest('.base-form-item')?.classList.remove('focused');
                }
            });
        });
    }
}
